/**
 * Exact money formatting for display.
 *
 * ## What this module deliberately is not
 *
 * It is not a money model. `NuraMoney` in `backend/shared/money/` is the money
 * model, and its own module documentation states that it is "an internal import
 * surface for Nura backend code … not a public SDK interface". Importing it
 * here would make client code depend on a backend internal, which
 * `docs/roadmap/PHASE_03_EARLY_FOUNDATION_DECISION.md` § Identifier boundary
 * warns against for identifiers and which applies with equal force to money.
 *
 * So this module holds no invariants and owns no amount. It formats minor units
 * it is handed, and the authority for whether those minor units are valid stays
 * where the architecture puts it.
 *
 * ## One authority for monetary scale
 *
 * The one thing it does need is the minor-unit exponent, and that is **not**
 * restated here. `iso-4217.generated.ts` is produced by
 * `frontend/scripts/generate-iso-4217.ts` from the same content-hashed SIX
 * snapshot the backend pins, and a test re-derives it to prove the two agree.
 * Reading exponents from ICU via `Intl.resolvedOptions()` would have been
 * easier and would have quietly introduced a second, unpinned source of
 * monetary truth.
 *
 * ## Exactness
 *
 * `CLAUDE.md` § Transaction integrity: "Money uses integer minor units or an
 * exact decimal type; never binary floating point."
 *
 * The amount is a `bigint` from end to end. It becomes a decimal *string* by
 * string manipulation, and that string — never a `number` — is what reaches
 * `Intl`. No value on this path is ever a double, so no value on this path can
 * be rounded by one. `format.test.ts` proves this with an amount that a double
 * demonstrably corrupts.
 */

/**
 * ECMA-402 Number Format Functions accept a string and interpret it as an exact
 * mathematical value, with no intermediate `Number` conversion. TypeScript's
 * bundled lib types still declare only `number | bigint`, so the widened
 * signature is asserted here, once, rather than at every call site. The
 * behaviour this depends on is covered by a test.
 */
import { ISO_4217_MINOR_UNITS, type IsoCurrencyCode } from "./iso-4217.generated.ts";

interface ExactNumberFormat {
  format(value: string): string;
  formatToParts(value: string): Intl.NumberFormatPart[];
}

/**
 * Raised when a currency has no minor-unit count in the pinned ISO 4217 source.
 *
 * Not a rendering failure to paper over: a currency Nura cannot scale exactly is
 * a currency Nura must not display an amount in. The interface surfaces the
 * error rather than guessing an exponent.
 */
export class DisplayCurrencyError extends Error {
  constructor(currency: string) {
    super(`no pinned ISO 4217 minor-unit count for currency: ${currency}`);
    this.name = "DisplayCurrencyError";
  }
}

/** True when the pinned snapshot gives `currency` a minor-unit count. */
export function hasDisplayExponent(currency: string): currency is IsoCurrencyCode {
  return Object.hasOwn(ISO_4217_MINOR_UNITS, currency);
}

/**
 * Minor-unit digit count for a currency, from the pinned ISO 4217 snapshot.
 *
 * The table is generated from `backend/shared/money/src/currency.ts`, so there
 * is exactly one authority for monetary scale in this repository and the
 * frontend restates none of it.
 */
export function displayExponent(currency: string): number {
  if (!hasDisplayExponent(currency)) throw new DisplayCurrencyError(currency);
  return ISO_4217_MINOR_UNITS[currency];
}

/**
 * Render minor units as an exact decimal string.
 *
 * Pure string and `bigint` work. `123456n` at exponent 2 yields the digits
 * `1234`, a period, and the digits `56`; at exponent 0 it yields `123456`.
 */
export function toExactDecimalString(amountMinor: bigint, exponent: number): string {
  if (typeof amountMinor !== "bigint") {
    throw new TypeError("amountMinor must be a bigint in minor units");
  }
  if (amountMinor < 0n) {
    throw new RangeError("amountMinor must not be negative");
  }
  if (!Number.isSafeInteger(exponent) || exponent < 0) {
    throw new RangeError("exponent must be a non-negative integer");
  }

  const digits = amountMinor.toString();
  if (exponent === 0) return digits;

  const padded = digits.padStart(exponent + 1, "0");
  const split = padded.length - exponent;
  return `${padded.slice(0, split)}.${padded.slice(split)}`;
}

function currencyFormatter(locale: string, currency: string, exponent: number): ExactNumberFormat {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }) as unknown as ExactNumberFormat;
}

/** Fully formatted currency string for the given locale. */
export function formatMoney(amountMinor: bigint, currency: string, locale: string): string {
  const exponent = displayExponent(currency);
  return currencyFormatter(locale, currency, exponent).format(
    toExactDecimalString(amountMinor, exponent),
  );
}

/**
 * A money value split for typographic treatment.
 *
 * The amount composer sets the currency symbol and the minor units smaller than
 * the major units, which needs the pieces separately. Splitting is done from
 * `Intl` parts rather than by slicing a formatted string, so locales that put
 * the symbol last, use a different decimal separator, or group digits in
 * non-Western patterns all come out right.
 */
export interface MoneyDisplayParts {
  /** Currency symbol plus any adjacent spacing, in its locale position. */
  readonly symbol: string;
  /** Grouped integer digits. */
  readonly major: string;
  /** Decimal separator plus fraction digits. Empty for exponent-0 currencies. */
  readonly minor: string;
  /** True when the symbol precedes the digits in this locale. */
  readonly symbolLeads: boolean;
}

export function formatMoneyParts(
  amountMinor: bigint,
  currency: string,
  locale: string,
): MoneyDisplayParts {
  const exponent = displayExponent(currency);
  const parts = currencyFormatter(locale, currency, exponent).formatToParts(
    toExactDecimalString(amountMinor, exponent),
  );

  let symbol = "";
  let major = "";
  let minor = "";
  let seenDigits = false;
  let symbolLeads = false;

  for (const part of parts) {
    switch (part.type) {
      case "currency":
        symbol += part.value;
        if (!seenDigits) symbolLeads = true;
        break;
      case "integer":
      case "group":
      case "minusSign":
        major += part.value;
        seenDigits = true;
        break;
      case "decimal":
      case "fraction":
        minor += part.value;
        seenDigits = true;
        break;
      case "literal":
        // Spacing around the symbol belongs with the symbol; anything else is
        // between digits and is dropped rather than guessed at.
        if (!seenDigits || symbol !== "") symbol += part.value;
        break;
      default:
        break;
    }
  }

  return { symbol: symbol.trim(), major, minor, symbolLeads };
}
